import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ArrowRight } from "lucide-react";
import axios from "axios";
import { API, thumbUrl } from "../config";
import { ProductLightbox } from "./ProductLightbox";
import { useLang, DISPLAY_EN, productGroupLabel } from "../i18n";

// Eş anlamlı grupları (norm edilmiş) — ÇİFT YÖNLÜ çalışır:
// üründe grubun herhangi bir üyesi geçiyorsa diğer üyeler de aranabilir hale gelir
// (örn. "tik" yazınca TEAK, "sıkaymore" yazınca SCYMORE bulunur)
const SYN_GROUPS = [
  ["mese", "oak"], ["ceviz", "walnut", "valnat"], ["kiraz", "cherry", "ceri"],
  ["disbudak", "ash"], ["kayin", "beech", "bic"], ["akcaagac", "maple", "mepil"],
  ["kus gozu", "birds eye", "kusgozu", "bird eye"],
  ["okaliptus", "eucalyptus", "okaliptis"], ["kavak", "poplar"],
  ["abanoz", "ebony", "eboni"], ["sedir", "cedar"], ["kestane", "chestnut"],
  ["gurgen", "hornbeam"], ["hus", "birch", "borc"], ["armut", "pear"],
  ["ladin", "spruce"], ["cam agaci", "pine"], ["palmiye", "palm"],
  ["tik", "teak", "tiik", "tek agaci"], ["akaju", "mahogany", "maun", "mahagoni"],
  ["limon", "lemon"], ["zeytin", "olive"], ["karaagac", "elm"],
  ["ihlamur", "lime", "linden"],
  ["sycamore", "scymore", "sycmore", "sikamor", "sikamore", "sikaymor"],
  ["wenge", "venge"], ["okoume", "okume"], ["sapelli", "sapeli"],
  ["zebrano", "zebrawood", "zebra"], ["anigre", "anegre", "aniegre"],
  ["iroko", "irako"], ["bubinga", "bubing"], ["padauk", "paduk", "padok"],
  ["kontrplak", "kontraplak", "plywood", "playvut"],
  ["kaplama", "veneer", "vinir"], ["kereste", "lumber", "timber"],
  ["mese fume", "fume oak", "smoked oak"], ["koyu", "dark"], ["acik", "light"],
].map((g) => g.map((w) => w.trim()));

let productCache = null;

const norm = (s) =>
  (s || "")
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i").replace(/ş/g, "s").replace(/ğ/g, "g")
    .replace(/ü/g, "u").replace(/ö/g, "o").replace(/ç/g, "c")
    .replace(/[-_./,]+/g, " ")
    .replace(/\s+/g, " ");

// Levenshtein mesafesi — yazım hatası toleransı için
const lev = (a, b) => {
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > 3) return 99;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
};

const maxDistFor = (len) => (len >= 8 ? 3 : len >= 4 ? 2 : len >= 3 ? 1 : 0);

// Fonetik katlama — Türkçe okunuşla yazılan yabancı adları eşleştirir (afromozya → afromosia)
const fold = (s) =>
  s
    .replace(/ph/g, "f")
    .replace(/x/g, "ks")
    .replace(/q/g, "k")
    .replace(/w/g, "v")
    .replace(/z/g, "s")
    .replace(/c/g, "k")
    .replace(/y/g, "i")
    .replace(/(.)\1+/g, "$1");

// Mobilde klavyenin açılması için: kullanıcı dokunuşu anında geçici inputa odaklan
export const prefocusKeyboard = () => {
  const tmp = document.createElement("input");
  tmp.type = "text";
  tmp.style.cssText = "position:fixed;top:0;left:0;opacity:0;height:1px;width:1px;font-size:16px;";
  document.body.appendChild(tmp);
  tmp.focus({ preventScroll: true });
  setTimeout(() => tmp.remove(), 800);
};

const scoreProduct = (hay, words, terms) => {
  const fhay = fold(hay);
  let total = 0;
  for (const t of terms) {
    if (hay.includes(t)) { total += 100; continue; }
    const ft = fold(t);
    if (fhay.includes(ft)) { total += 85; continue; }
    let best = 99;
    for (const w of words) {
      const fw = fold(w);
      best = Math.min(best, lev(ft, fw));
      if (fw.length > ft.length + 1) best = Math.min(best, lev(ft, fw.slice(0, ft.length + 1)));
      if (best === 0) break;
    }
    if (best <= maxDistFor(ft.length)) total += 50 - best * 10;
    else return -1;
  }
  return total;
};

// Belirgin, çerçeveli arama çubuğu tetikleyicisi — yanlarında çizgilerle
export const SearchTrigger = ({ className = "", light = false }) => {
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  return (
    <>
      <div className={`max-w-2xl mx-auto flex items-center gap-4 md:gap-5 ${className}`}>
        <span className="hidden sm:block h-px flex-1 bg-gradient-to-r from-transparent to-[#7BA05B]/70" />
        <button
          type="button"
          data-testid="page-search-bar"
          onClick={() => { prefocusKeyboard(); setOpen(true); }}
          className={`group flex items-center gap-3 md:gap-4 w-full sm:w-auto sm:min-w-[380px] md:min-w-[440px] rounded-full border px-6 md:px-7 py-4 md:py-[18px] text-left transition-all duration-400 ${
            light
              ? "border-black/15 bg-white hover:border-[#7BA05B] shadow-[0_4px_24px_rgba(0,0,0,0.08)]"
              : "border-[#F2EFEB]/30 bg-[#0D0C0B]/45 backdrop-blur-md hover:border-[#7BA05B] hover:bg-[#0D0C0B]/65 shadow-[0_8px_40px_rgba(0,0,0,0.35)]"
          }`}
        >
          <Search size={19} strokeWidth={1.75} className="text-[#7BA05B] shrink-0 group-hover:scale-110 transition-transform duration-300" />
          <span className={`flex-1 font-sans text-sm md:text-[15px] transition-colors truncate ${light ? "text-[#6B6560] group-hover:text-[#1A1715]" : "text-[#C9C4BD] group-hover:text-[#F2EFEB]"}`}>
            {t("search.triggerText")}
          </span>
          <span className={`hidden md:inline font-sans uppercase tracking-[0.2em] text-[9px] rounded-full px-3 py-1.5 transition-colors ${light ? "text-[#6B6560] border border-black/15 group-hover:border-[#7BA05B]/50 group-hover:text-[#7BA05B]" : "text-[#8C8681] border border-white/15 group-hover:border-[#7BA05B]/50 group-hover:text-[#7BA05B]"}`}>
            {t("search.triggerBtn")}
          </span>
        </button>
        <span className="hidden sm:block h-px flex-1 bg-gradient-to-l from-transparent to-[#7BA05B]/70" />
      </div>
      <GlobalSearch open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export const prefetchProducts = () => {
  if (productCache && productCache.length > 0) return;
  axios
    .get(`${API}/products`, { timeout: 20000 })
    .then((res) => { productCache = res.data; })
    .catch(() => {});
};

export const GlobalSearch = ({ open, onClose }) => {
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState(productCache || []);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const { t, dn } = useLang();
  const lightboxRef = useRef(null);
  lightboxRef.current = lightbox;
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setLightbox(null);
    setTimeout(() => inputRef.current?.focus(), 250);
    let cancelled = false;
    const load = (attempt = 0) => {
      setLoading(true);
      axios
        .get(`${API}/products`, { timeout: 20000 })
        .then((res) => {
          if (cancelled) return;
          productCache = res.data;
          setProducts(res.data);
          setLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt < 2) setTimeout(() => load(attempt + 1), 1500);
          else setLoading(false);
        });
    };
    if (productCache && productCache.length > 0) setProducts(productCache);
    else load();
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape" && lightboxRef.current === null) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      cancelled = true;
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const results = useMemo(() => {
    const q = norm(query.trim());
    if (q.length < 2) return [];
    const terms = q.split(/\s+/);
    return products
      .map((p) => {
        const alias = p.category === "Kaplama" ? "dogal" : "";
        const base = norm(`${p.name} ${p.category} ${p.subcategory || ""} ${p.tone || ""} ${alias}`);
        const enLabels = [DISPLAY_EN[p.category], DISPLAY_EN[p.subcategory], DISPLAY_EN[p.tone]].filter(Boolean).join(" ");
        const syn = SYN_GROUPS.filter((g) => g.some((w) => base.includes(w))).flat().join(" ");
        const hay = norm(`${base} ${enLabels} ${syn}`);
        return { p, score: scoreProduct(hay, hay.split(/\s+/), terms) };
      })
      .filter((r) => r.score >= 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 40)
      .map((r) => r.p);
  }, [query, products]);


  const goTo = (i) => setLightbox(i);

  // Sorgu değişince ürün detayını kapat; lightbox kapanınca kaydırmayı kilitli tut
  useEffect(() => { setLightbox(null); }, [query]);
  useEffect(() => {
    if (open && lightbox === null) document.body.style.overflow = "hidden";
  }, [open, lightbox]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="global-search-overlay"
          className="fixed inset-0 z-[110] flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div className="absolute inset-0 bg-white/[0.98] backdrop-blur-xl" onClick={onClose} />

          <motion.div
            initial={{ y: -24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -16, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 w-full max-w-3xl mx-auto px-5 pt-24 md:pt-32 flex flex-col min-h-0 flex-1"
          >
            <button
              data-testid="search-close"
              onClick={onClose}
              aria-label="Kapat"
              className="absolute top-6 right-5 md:top-8 md:right-0 w-11 h-11 rounded-full border border-[#2F4A26]/30 flex items-center justify-center text-[#2F4A26] hover:bg-[#2F4A26] hover:border-[#2F4A26] hover:text-white transition-all duration-300"
            >
              <X size={18} strokeWidth={1.75} />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-[#2F4A26]" />
              <span className="font-sans uppercase tracking-[0.25em] text-[10px] text-[#2F4A26]">{t("search.label")}</span>
            </div>

            <div className="relative">
              <Search size={20} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#2F4A26]" />
              <input
                ref={inputRef}
                data-testid="search-input"
                type="text"
                enterKeyHint="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                placeholder={t("search.placeholder")}
                className="w-full bg-transparent border-b-2 border-[#2F4A26]/25 focus:border-[#2F4A26] outline-none pl-9 pr-2 py-4 font-serif text-xl md:text-3xl text-[#1A1715] placeholder:text-[#6B6560]/50 placeholder:text-lg md:placeholder:text-xl transition-colors duration-300"
              />
            </div>

            <div className="mt-3 flex items-center justify-between">
              <span className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#6B6560]">
                {loading
                  ? t("search.loading")
                  : query.trim().length < 2
                  ? t("search.searchIn", products.length)
                  : t("search.results", results.length, results.length === 40)}
              </span>
            </div>

            <div data-testid="search-results" className="mt-6 flex-1 min-h-0 overflow-y-auto pb-16 -mx-1 px-1">
              {query.trim().length >= 2 && results.length === 0 && !loading && (
                <p data-testid="search-no-results" className="text-[#6B6560] text-sm mt-4">
                  {t("search.noResults", query)}
                </p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {results.map((p, i) => (
                  <motion.button
                    key={p.id}
                    data-testid={`search-result-${i}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.025, 0.3), duration: 0.3 }}
                    onClick={() => goTo(i)}
                    className="group flex items-center gap-4 p-2.5 rounded-xl bg-white hover:bg-[#2F4A26]/[0.05] ring-1 ring-black/10 hover:ring-[#2F4A26]/50 shadow-[0_2px_10px_rgba(0,0,0,0.05)] text-left transition-all duration-300"
                  >
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#F0EDE8] shrink-0">
                      {p.image && (
                        <img
                          src={thumbUrl(p.image)}
                          alt={p.name}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p translate="no" className="notranslate font-serif text-base text-[#1A1715] truncate leading-tight">{p.name}</p>
                      <p className="font-sans uppercase tracking-[0.18em] text-[9px] text-[#2F4A26] mt-1 truncate">
                        {productGroupLabel(p, dn)}
                      </p>
                    </div>
                    <ArrowRight size={16} strokeWidth={1.5} className="text-[#6B6560] group-hover:text-[#2F4A26] group-hover:translate-x-0.5 shrink-0 transition-all duration-300" />
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>

          <ProductLightbox
            products={results}
            index={lightbox}
            onClose={() => setLightbox(null)}
            onNavigate={setLightbox}
          />
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
